import { useState } from "react";
import { FileEdit, Forward, Reply, ReplyAll } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import {
  useForwardDraft,
  useReplyDraft,
  type EopMessageDetail,
} from "@/features/eop_mail/api/mailbox";
import { extractError } from "./sanitize";
import { RefineButton } from "./RefineButton";

/**
 * Pisanie odpowiedzi.
 *
 * Przycisk NIE nazywa się „Wyślij" i nie wysyła — tworzy KOPIĘ ROBOCZĄ w skrzynce.
 * To nie jest ostrożność interfejsu, tylko odwzorowanie zasady: z eop@ nic nie
 * wychodzi bez świadomego drugiego kliknięcia, a szkic ma być widoczny również
 * w Outlooku, żeby dało się go tam dokończyć.
 */
export function ReplyComposer({
  message,
  onCreated,
}: {
  message: EopMessageDetail;
  onCreated: (draftId: string) => void;
}) {
  const [mode, setMode] = useState<"" | "reply" | "replyAll" | "forward">("");
  const [body, setBody] = useState("");
  const [forwardTo, setForwardTo] = useState("");
  const [error, setError] = useState("");
  const createDraft = useReplyDraft();
  const forwardDraft = useForwardDraft();

  if (!mode) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" onClick={() => setMode("reply")}>
          <Reply size={13} className="mr-1" />
          Odpowiedz
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setMode("replyAll")}>
          <ReplyAll size={13} className="mr-1" />
          Odpowiedz wszystkim
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setMode("forward")}>
          <Forward size={13} className="mr-1" />
          Prześlij
        </Button>
      </div>
    );
  }

  const close = () => {
    setMode("");
    setError("");
  };

  const submit = () => {
    setError("");
    if (mode === "forward") {
      const recipients = forwardTo
        .split(/[,;\s]+/)
        .map((v) => v.trim())
        .filter(Boolean);
      if (recipients.length === 0) {
        setError("Podaj, komu przekazać wiadomość.");
        return;
      }
      forwardDraft.mutate(
        { id: message.id, to: recipients, body_html: body },
        {
          onSuccess: (draft) => {
            close();
            setBody("");
            setForwardTo("");
            onCreated(draft.id);
          },
          onError: (err) => setError(extractError(err)),
        },
      );
      return;
    }
    createDraft.mutate(
      { id: message.id, body_html: body, reply_all: mode === "replyAll" },
      {
        onSuccess: (draft) => {
          close();
          setBody("");
          onCreated(draft.id);
        },
        onError: (err) => setError(extractError(err)),
      },
    );
  };
  const pending = createDraft.isPending || forwardDraft.isPending;

  return (
    <div className="card p-4">
      <div className="-mx-4 mb-3 border-b border-[var(--color-border)] px-4 pb-2">
        <span className="section-label">
          {mode === "forward"
            ? "Przekazanie dalej"
            : mode === "replyAll"
              ? "Odpowiedź do wszystkich"
              : "Odpowiedź"}
        </span>
      </div>
      <div className="space-y-3">
      {mode === "forward" && (
        <Input
          label="Do"
          value={forwardTo}
          onChange={(e) => setForwardTo(e.target.value)}
          placeholder="komu przekazać (kilka adresów po przecinku)"
          autoFocus
        />
      )}
      <RichTextEditor
        value={body}
        onChange={setBody}
        placeholder={
          mode === "forward"
            ? "Komentarz do przekazania (opcjonalnie). Podpis dokleja się automatycznie."
            : "Treść odpowiedzi. Podpis Twoim imieniem i nazwiskiem dokleja się automatycznie."
        }
      />
      {error && <p className="text-[11px] text-[var(--color-danger)]">{error}</p>}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button size="sm" onClick={submit} loading={pending}>
          <FileEdit size={13} className="mr-1" />
          Zapisz kopię roboczą
        </Button>
        <RefineButton body={body} onRefined={setBody} />
        <Button variant="ghost" size="sm" onClick={close}>
          Anuluj
        </Button>
        <span className="ml-auto text-[11px] text-[var(--color-text-muted)]">
          {mode === "forward"
            ? "Oryginał i załączniki jadą razem. Szkic widać też w Outlooku."
            : "Szkic widać też w Outlooku. Nic nie wychodzi bez „Wyślij”."}
        </span>
      </div>
      </div>
    </div>
  );
}
